import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Loader } from '@quero-delivery/quero-components-web';
import { toast } from 'react-toastify';
import { DefaultPillInput } from '@/_components/default-pill-input/default-pill-input';
import { FareDistrict } from '../fare-district/fare-district';
import styles from './fare-districts-list.module.scss';
import {
  faresMoneyInputFormat,
  convertMoneyInputFormatToFloatWithNegative,
} from '@/Utils/FnUtils';
import {
  type District,
  type FareType,
  type TypeFranchiseForm,
} from '../../types';
import { useImportNeighborhoods } from '../../api/get-import-neighborhoods';

interface FareDistrictsListProps {
  isOnAccordion: boolean;
  cityId?: string;
  districts: District[];
  fareType: FareType;
}

export const FareDistrictsList = ({
  isOnAccordion,
  cityId,
  districts,
  fareType,
}: FareDistrictsListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [logFee, setLogFee] = useState(0);
  const [deliveryValue, setDeliveryValue] = useState(0);
  const [queroDeliveryCityId, setQueroDeliveryCityId] = useState('');
  const [canFetchNeighborhoods, setCanFetchNeighborhoods] = useState(false);

  const { setValue: setFormValue } = useFormContext<TypeFranchiseForm>();

  const {
    data: neighborhoods,
    isFetching: isLoadingNeighborhoods,
    isFetched: isFetchedNeighborhoods,
  } = useImportNeighborhoods(
    {
      generalCityId: queroDeliveryCityId,
    },
    {
      enabled: !!queroDeliveryCityId && canFetchNeighborhoods,
    },
  );

  const resetInputField = () => {
    setQueroDeliveryCityId('');
    setCanFetchNeighborhoods(false);
  };

  useEffect(() => {
    if (isFetchedNeighborhoods) {
      resetInputField();
    }
  }, [isFetchedNeighborhoods]);

  useEffect(() => {
    if (neighborhoods && !neighborhoods?.length) {
      toast.info('Nenhum bairro encontrado para importação');
      return;
    }

    if (neighborhoods && neighborhoods.length) {
      setFormValue(
        'bairros',
        [
          ...neighborhoods.map((neighborhood, index) => ({
            ...neighborhood,
            logFee: neighborhood.logFee || 0,
            deliveryValue: neighborhood.deliveryValue || 0,
            latitude: neighborhood.location?.latitude,
            longitude: neighborhood.location?.longitude,
            index,
          })),
        ],
        { shouldDirty: true },
      );
    }
  }, [neighborhoods]);

  const actualDistricts = (neighborhoods ?? districts).map(
    (district, index) => ({
      ...district,
      index,
    }),
  );

  const districtsFound = actualDistricts.filter(
    district =>
      district?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) &&
      (!cityId || district.cityId === cityId),
  );

  const isCityFare = fareType === 'City';
  const isCityWithoutDistricts = !districtsFound.length && !searchTerm;
  const hasNotFindDistricts = districtsFound.length === 0 && searchTerm !== '';

  const applyValuesToAll = () => {
    districtsFound.forEach((_district, index) => {
      setFormValue(`bairros.${index}.logFee`, logFee, {
        shouldDirty: true,
      });
      setFormValue(`bairros.${index}.deliveryValue`, deliveryValue, {
        shouldDirty: true,
      });
    });
  };

  const importNeighborhoods = () => {
    setCanFetchNeighborhoods(true);
  };

  return (
    <div
      className={`${styles.container} ${
        isOnAccordion ? styles.accordionContainer : ''
      }`}
    >
      {isCityWithoutDistricts ? (
        <div className={styles.notFoundContainer}>
          {!isLoadingNeighborhoods ? (
            <>
              <p>
                {`Nenhum bairro encontrado. ${
                  isCityFare
                    ? 'Deseja importar os bairros do Quero Delivery?'
                    : ''
                }`}
              </p>
              {isCityFare && (
                <div className={styles.notFoundInputContainer}>
                  <DefaultPillInput
                    value={queroDeliveryCityId}
                    placeholder="ID da cidade no Quero Delivery"
                    onChange={e => setQueroDeliveryCityId(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={importNeighborhoods}
                    disabled={!queroDeliveryCityId}
                  >
                    Importar bairros
                  </button>
                </div>
              )}
            </>
          ) : (
            <Loader size="md" />
          )}
        </div>
      ) : (
        <>
          <span>Aplicar taxa em todos os bairros</span>
          <div className={styles.inputsContainer}>
            <DefaultPillInput
              containerClassName={`${styles.inputContainer}`}
              label="Taxa do Log"
              leftTag="R$"
              value={logFee}
              onChange={e => {
                setLogFee(
                  convertMoneyInputFormatToFloatWithNegative(e.target.value),
                );
              }}
            />
            <DefaultPillInput
              containerClassName={`${styles.inputContainer}`}
              label="Valor"
              leftTag="R$"
              value={deliveryValue}
              onChange={e => {
                setDeliveryValue(faresMoneyInputFormat(e.target.value));
              }}
            />

            <button type="button" onClick={applyValuesToAll}>
              aplicar
            </button>
          </div>

          <DefaultPillInput
            containerClassName={`${styles.searchContainer}`}
            placeholder="Buscar bairro"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          {hasNotFindDistricts ? (
            <div className={styles.notFoundContainer}>
              <p>Nenhum bairro encontrado</p>
            </div>
          ) : (
            districtsFound.map((district, index) => {
              const actualIndex = district?.index ?? index;
              return (
                <FareDistrict
                  key={district?.generalNeighborhoodId ?? actualIndex}
                  index={actualIndex}
                  canSwitchActiveStatus={isCityFare}
                />
              );
            })
          )}
        </>
      )}
    </div>
  );
};
